/**
 * A FileDrop room: several devices on one network, connected in a WebRTC mesh.
 *
 * Every device holds one peer connection and one ordered data channel to every
 * other device. Files are never pushed: a device announces what it is sharing
 * (name, size, type), and another device *pulls* a file by asking its owner
 * over their data channel. Bytes only ever flow between those two.
 *
 * Design notes that matter:
 *
 *  - Exactly one side of a pair offers, and which one is decided by comparing
 *    the two device ids rather than by who arrived first. Order-of-arrival works
 *    for a first meeting but not for a *rebuild*, where both sides can decide at
 *    the same moment that the connection is dead; comparing ids is the same
 *    answer on both devices however they got there, so glare cannot happen.
 *  - Chunks are 16 KiB. Larger chunks hit SCTP message-size limits on some
 *    platforms, and Chrome's send buffer misbehaves well before the spec limit.
 *  - Sending is paced with bufferedAmountLow rather than a timer. Ignoring
 *    backpressure and pushing a whole file into the data channel is the classic
 *    way to crash the tab on a large transfer.
 *  - One transfer at a time per direction per pair, queued. Simple, and the
 *    channel is ordered so there is no interleaving to untangle.
 *  - There is no TURN relay, by design: file bytes must never pass through a
 *    server of ours. Connections use public STUN only, which learns a device's
 *    public IP and never sees payload. The cost is real and permanent — two
 *    networks that block direct device-to-device traffic cannot be joined, and
 *    the UI says so plainly rather than spinning forever.
 *  - Every room has a four-character code, and the worker puts a device in
 *    whichever room its network is already using — so devices on one Wi-Fi meet
 *    with nothing to type, and all show the same code to pass to someone who is
 *    not. The code arrives in `welcome`; the app puts it in the URL so the page
 *    and its QR are always joinable. Arriving *with* a code overrides all that.
 *  - Moving between rooms takes the peers already connected along, since a peer
 *    connection is independent of the signalling socket that arranged it.
 *  - Each device picks a UUID up front and brings it to the signalling server,
 *    so a dropped signalling socket can be resumed without the room seeing a
 *    new device. It lives in sessionStorage, because a phone that backgrounds
 *    this page long enough has the whole tab discarded and reloaded, and a fresh
 *    id would walk back in as a stranger while the ghost of the old one sat in
 *    everyone's list. The lifetime is exactly right: it survives a reload and a
 *    restore, and it dies with the tab.
 *  - Presence is proven, not assumed. A data channel does NOT close when the
 *    device at the other end is frozen or killed — SCTP has no keepalive of its
 *    own, and ICE usually parks at `disconnected` rather than failing — so the
 *    mesh pings, and a peer that stops answering is dropped. Without this, a
 *    suspended phone stays in the room forever, still advertising files that
 *    nobody can fetch.
 *  - Every transfer has a deadline. `readyState` reading "open" proves only that
 *    *this* end is willing, so a request can vanish into a dead channel. A
 *    transfer that never starts, or stops moving, fails and says why, instead of
 *    sitting at "receiving 0%" for the life of the page.
 *  - The page can be frozen at any moment and thawed much later. Nothing here
 *    may treat elapsed time as evidence about a peer without checking that its
 *    own clock was running — see `sweep`.
 */

import { id as newId } from "@suite/core";
import {
  deviceKind,
  deviceLabel,
  disambiguate,
  funnyName,
  type DeviceKind,
} from "./device.ts";

const CHUNK_SIZE = 16 * 1024;
const LOW_WATER = 256 * 1024;
const HIGH_WATER = 1024 * 1024;
/** Progress events are coalesced to this interval so React isn't re-rendered per chunk. */
const PROGRESS_INTERVAL_MS = 80;

export const STUN_SERVERS: RTCIceServer[] = [
  { urls: ["stun:stun.cloudflare.com:3478", "stun:stun.l.google.com:19302"] },
];

/** Reconnect to the room server with a gentle backoff; give up after this many tries. */
const RECONNECT_DELAYS_MS = [1000, 2000, 4000, 8000, 15000];

/** How often the mesh checks that everyone in it is still really there. */
const SWEEP_INTERVAL_MS = 4000;
/** A peer that was connected and has now gone quiet this long has gone. */
const SILENCE_MS = 15000;
/** A peer the room says has left gets this long to prove otherwise. */
const DEPARTED_GRACE_MS = 6000;
/** How long a requested file may take to start arriving. */
const START_TIMEOUT_MS = 12000;
/** How long a transfer may go with no byte moving before it is called dead. */
const STALL_TIMEOUT_MS = 30000;
/**
 * Losing a peer we had is a reason to re-join the room server and see who is
 * really still here — but only this often, so a bad network cannot turn into a
 * stream of reconnects.
 */
const REJOIN_COOLDOWN_MS = 30000;

/**
 * Codes are minted by the worker, not here: minting is also a *claim* on the
 * room the code names, so two networks can never be handed the same one. What
 * the browser needs is only to read one a person typed, and to recognise the
 * shape before bothering the server.
 *
 * The alphabet in use has no i, l or o, so nothing in a code can be confused
 * with 1 or 0; `normalizeRoomCode` folds those back in.
 */
export const ROOM_CODE_LENGTH = 4;

/** What someone typed, as the code it can only have meant. */
export function normalizeRoomCode(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[il]/g, "1")
    .replace(/o/g, "0")
    .replace(/[^0-9a-z]/g, "");
}

/** The shape the worker accepts, so a mistyped code fails here and not there. */
export function isRoomCode(value: string): boolean {
  return new RegExp(`^[a-z0-9]{${ROOM_CODE_LENGTH}}$`).test(value);
}

/** Where this tab keeps its device id, so a reload rejoins as the same device. */
const IDENTITY_KEY = "otterdrop:device";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * This tab's device id: minted once, kept for as long as the tab is open.
 *
 * Storage can be unavailable (private mode, storage switched off), and a
 * per-load id still works there — it just cannot survive a reload, which is the
 * pre-existing behaviour rather than a new failure.
 */
function deviceIdentity(): string {
  try {
    const held = sessionStorage.getItem(IDENTITY_KEY);
    if (held && UUID.test(held)) return held;
    const minted = crypto.randomUUID();
    sessionStorage.setItem(IDENTITY_KEY, minted);
    return minted;
  } catch {
    return crypto.randomUUID();
  }
}

export interface FileMeta {
  id: string;
  name: string;
  size: number;
  mime: string;
}

export interface SharedFile extends FileMeta {
  ownerId: string;
  ownerName: string;
  mine: boolean;
}

export type PeerState = "connecting" | "connected" | "failed";

export interface Peer {
  id: string;
  /** Friendly two-word name, e.g. "Brave Otter". */
  name: string;
  /** Device label, e.g. "Mac · Chrome". */
  label: string;
  kind: DeviceKind;
  state: PeerState;
  fileCount: number;
}

export interface Download {
  fileId: string;
  received: number;
  size: number;
  done: boolean;
  failed: string | null;
  blobUrl: string | null;
  /** The assembled file, once `done`. Kept so several can be zipped together. */
  blob: Blob | null;
}

export type RoomStatus = "idle" | "connecting" | "open" | "failed" | "closed";

export interface RoomSnapshot {
  status: RoomStatus;
  /** The invite code this room is using, or null when it is this network's room. */
  code: string | null;
  /** Explanation for `failed` / `closed`, in plain words. */
  detail: string | null;
  selfId: string | null;
  selfName: string;
  selfLabel: string;
  selfKind: DeviceKind;
  peers: Peer[];
  files: SharedFile[];
  downloads: Record<string, Download>;
  /** How many devices have finished taking each of my files. */
  takenBy: Record<string, number>;
  /** How many devices are currently pulling each of my files. */
  sendingTo: Record<string, number>;
}

type Signal =
  | { kind: "welcome"; id: string; peers: string[]; code?: string }
  | { kind: "peer-joined"; id: string }
  | { kind: "peer-left"; id: string }
  | { kind: "offer"; sdp: RTCSessionDescriptionInit; from: string }
  | { kind: "answer"; sdp: RTCSessionDescriptionInit; from: string }
  | { kind: "ice"; candidate: RTCIceCandidateInit; from: string };

type Control =
  | { kind: "hello"; name: string; label: string; deviceKind: DeviceKind; files: FileMeta[] }
  | { kind: "files"; files: FileMeta[] }
  | { kind: "request"; id: string }
  | { kind: "begin"; meta: FileMeta }
  | { kind: "end"; id: string }
  | { kind: "cancel"; id: string; reason: string }
  | { kind: "moved"; code: string | null }
  | { kind: "ping" }
  | { kind: "pong" };

interface Link {
  id: string;
  name: string | null;
  label: string;
  kind: DeviceKind;
  state: PeerState;
  pc: RTCPeerConnection | null;
  channel: RTCDataChannel | null;
  files: FileMeta[];
  pendingIce: RTCIceCandidateInit[];
  /** The file currently arriving from this peer. */
  incoming: { meta: FileMeta; chunks: ArrayBuffer[]; bytes: number } | null;
  /** File ids this peer has asked for, served one at a time. */
  queue: string[];
  sending: string | null;
  /** When anything was last heard from this peer. The real presence signal. */
  lastSeen: number;
  /** Set when the room says this peer left while its channel still claims open. */
  departedAt: number | null;
  /**
   * Whether this pair ever had a working channel. A link that has never opened
   * is a connection that failed to establish — "adrift", and permanent — and
   * must not be swept away, or the UI loses the one honest thing it can say.
   */
  wasOpen: boolean;
}

export const IDLE_SNAPSHOT: RoomSnapshot = {
  status: "idle",
  code: null,
  detail: null,
  selfId: null,
  selfName: "",
  selfLabel: "",
  selfKind: "computer",
  peers: [],
  files: [],
  downloads: {},
  takenBy: {},
  sendingTo: {},
};

export class RoomSession {
  private ws: WebSocket | null = null;
  private readonly links = new Map<string, Link>();
  private readonly mine = new Map<string, { file: File; meta: FileMeta }>();
  private readonly downloads = new Map<string, Download>();
  private readonly taken = new Map<string, Set<string>>();
  private readonly listeners = new Set<() => void>();

  private status: RoomStatus = "idle";
  private detail: string | null = null;
  private readonly selfId: string = deviceIdentity();
  private code: string | null;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private sweepTimer: ReturnType<typeof setInterval> | null = null;
  /** When the sweep last ran, so a tab that was frozen recognises it on the way back. */
  private lastSweep = 0;
  /** When we last re-joined the room server off our own bat; see REJOIN_COOLDOWN_MS. */
  private lastRejoin = 0;
  /** Files asked for and not yet finished, with the moment each is given up on. */
  private readonly awaiting = new Map<string, { linkId: string; deadline: number }>();
  /**
   * Whether a socket has ever reached the room (received `welcome`). It tells a
   * socket close apart: before it, a close is a failure to connect; after it, a
   * close is a drop worth reconnecting for. Without this, the error event that
   * precedes a failed first connect flips the state and the close is misread as
   * a drop — reconnecting five times before giving up instead of failing fast.
   */
  private everOpen = false;
  /**
   * Bumped by every connect() and leave(). React StrictMode mounts the effect
   * twice (connect, leave, connect); the generation lets a superseded connect's
   * async tail bail out, so we never open two sockets or revive a real teardown.
   */
  private generation = 0;
  private readonly selfName: string;
  private readonly selfLabel: string;
  private readonly selfKind: DeviceKind;
  private closed = false;
  private lastProgressEmit = 0;
  private snapshot: RoomSnapshot;

  constructor(
    private readonly signalUrl: string,
    code?: string | null,
    name?: string,
  ) {
    this.code = code ?? null;
    const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
    this.selfLabel = name ?? deviceLabel(ua);
    this.selfName = funnyName(this.selfId);
    this.selfKind = deviceKind(ua);
    this.snapshot = this.build();
  }

  // ---- store surface (for useSyncExternalStore) -----------------------------

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getSnapshot(): RoomSnapshot {
    return this.snapshot;
  }

  private emit(): void {
    this.snapshot = this.build();
    for (const listener of this.listeners) listener();
  }

  private emitProgress(): void {
    const now = performance.now();
    if (now - this.lastProgressEmit < PROGRESS_INTERVAL_MS) return;
    this.lastProgressEmit = now;
    this.emit();
  }

  private build(): RoomSnapshot {
    const links = [...this.links.values()];
    const names = disambiguate(links.map((l) => l.name ?? "Joining…"));
    const peers: Peer[] = links.map((link, i) => ({
      id: link.id,
      name: names[i] ?? link.id,
      label: link.label,
      kind: link.kind,
      state: link.state,
      fileCount: link.files.length,
    }));

    const files: SharedFile[] = [];
    for (const { meta } of this.mine.values()) {
      files.push({ ...meta, ownerId: this.selfId, ownerName: "You", mine: true });
    }
    links.forEach((link, i) => {
      for (const meta of link.files) {
        files.push({ ...meta, ownerId: link.id, ownerName: names[i] ?? link.id, mine: false });
      }
    });

    const takenBy: Record<string, number> = {};
    for (const [fileId, set] of this.taken) takenBy[fileId] = set.size;
    const sendingTo: Record<string, number> = {};
    for (const link of links) {
      if (link.sending) sendingTo[link.sending] = (sendingTo[link.sending] ?? 0) + 1;
    }

    return {
      status: this.status,
      code: this.code,
      detail: this.detail,
      selfId: this.selfId,
      selfName: this.selfName,
      selfLabel: this.selfLabel,
      selfKind: this.selfKind,
      peers,
      files,
      downloads: Object.fromEntries(this.downloads),
      takenBy,
      sendingTo,
    };
  }

  // ---- signalling -----------------------------------------------------------

  async connect(): Promise<void> {
    // Revive a session that a prior (StrictMode dry-run) cleanup closed.
    this.closed = false;
    // Still bumped even though nothing is awaited here: leave() reads it to
    // invalidate a connect it superseded.
    this.generation += 1;
    this.reconnectAttempt = 0;
    this.everOpen = false;
    this.status = "connecting";
    this.detail = null;
    this.emit();

    // There is nothing to fetch before connecting: with no relay, the ICE
    // servers are a constant rather than credentials the worker has to mint.
    // The socket opens straight away, so joining is a round trip faster.
    this.startSweep();
    this.openSocket();
  }

  /**
   * The page is in front of a person again — restored from the back/forward
   * cache, un-frozen, or simply switched back to.
   *
   * A phone can suspend this page for minutes. While it is suspended no timer
   * runs, the signalling socket dies quietly, and peer connections rot; nothing
   * in the page notices, because noticing is itself a timer. So on the way back,
   * re-prove the room rather than trusting what is on screen.
   *
   * The silence while we were away says nothing about anybody else, so every
   * peer's clock is reset before anything is asked of it — otherwise the first
   * sweep after a thaw would evict a room full of perfectly live devices.
   */
  resume(): void {
    if (this.closed) {
      void this.connect();
      return;
    }
    const now = Date.now();
    for (const link of this.links.values()) {
      link.lastSeen = now;
      this.control(link, { kind: "ping" });
    }
    this.lastSweep = now;
    this.startSweep();
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }
    this.rejoin();
  }

  /**
   * Open a fresh signalling socket now, discarding any backoff. The Durable
   * Object replaces a socket that carries an id it already knows, so this costs
   * one connection and never makes the room think this device arrived twice.
   */
  private rejoin(): void {
    if (this.closed) return;
    this.lastRejoin = Date.now();
    // Detach first, so the old socket's onclose sees it has been superseded and
    // does not schedule a reconnect on top of this one.
    const previous = this.ws;
    this.ws = null;
    this.reconnectAttempt = 0;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    try {
      previous?.close(1000, "Rejoining");
    } catch {
      /* already gone */
    }
    // A routine re-join of a room we are still in should not flicker the UI
    // back to "paddling in…"; only say so when we really are not in one.
    if (this.status !== "open") {
      this.status = "connecting";
      this.detail = null;
    }
    this.emit();
    this.openSocket();
  }

  /**
   * Move this device to `code` (or back to its network room when null), and
   * take the devices already here along. Peer connections are direct and
   * outlive the signalling socket, so nothing in flight is interrupted: only
   * the socket that introduces *new* devices changes rooms.
   */
  moveTo(code: string | null): void {
    if (code === this.code || this.closed) return;
    this.startSweep();
    this.code = code;
    for (const link of this.links.values()) this.control(link, { kind: "moved", code });

    // Detach before closing so the old socket's onclose sees it is superseded
    // and does not schedule a reconnect to the room we just left.
    const previous = this.ws;
    this.ws = null;
    this.everOpen = false;
    this.reconnectAttempt = 0;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    try {
      previous?.close(1000, "Moved to another room");
    } catch {
      /* already gone */
    }
    this.status = "connecting";
    this.detail = null;
    this.emit();
    this.openSocket();
  }

  private openSocket(): void {
    const url = new URL(this.signalUrl);
    url.searchParams.set("id", this.selfId);
    if (this.code) url.searchParams.set("code", this.code);

    const ws = new WebSocket(url.toString());
    this.ws = ws;

    // A WebSocket error is always followed by a close; let onclose decide, so
    // the two events cannot disagree about what happened.
    ws.onerror = () => undefined;
    ws.onclose = (event) => {
      if (this.closed || this.ws !== ws) return;
      if (!this.everOpen) {
        // Never got into the room: a first-connect failure, not a drop.
        this.fail(
          event.reason && event.code === 1008
            ? event.reason
            : "Could not reach the room server. It may not be running, or the address is wrong.",
        );
        return;
      }
      // We were in the room. Peer connections are direct and keep working; only
      // new devices cannot find us until the socket is back. Try to get it back.
      const delay = RECONNECT_DELAYS_MS[this.reconnectAttempt];
      if (delay === undefined) {
        this.status = "closed";
        this.detail =
          "Lost the room server. Devices already here can keep exchanging files, but nobody new can join.";
        this.emit();
        return;
      }
      this.reconnectAttempt += 1;
      this.status = "connecting";
      this.detail = "Reconnecting to the room server…";
      this.emit();
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        if (!this.closed) this.openSocket();
      }, delay);
    };
    ws.onmessage = (event) => void this.onSignal(event);
  }

  private fail(detail: string): void {
    this.status = "failed";
    this.detail = detail;
    this.emit();
  }

  private signal(message: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(message));
  }

  private async onSignal(event: MessageEvent): Promise<void> {
    let signal: Signal;
    try {
      signal = JSON.parse(String(event.data)) as Signal;
    } catch {
      return;
    }

    switch (signal.kind) {
      case "welcome": {
        this.status = "open";
        this.detail = null;
        this.everOpen = true;
        this.reconnectAttempt = 0;
        // Usually the first time this device learns its code: it arrived
        // without one and the worker resolved its network's room. Keeping it
        // means a reconnect returns to this room rather than re-resolving.
        const given = signal.code ? normalizeRoomCode(signal.code) : "";
        if (isRoomCode(given)) this.code = given;
        // Reach out to everyone already here that we do not have a working
        // connection to. After a reconnect most are already linked and are
        // skipped; the rest are rebuilt from scratch, because a peer connection
        // that has failed or been closed never recovers on its own.
        for (const peerId of signal.peers) {
          if (peerId === this.selfId) continue;
          await this.reach(peerId);
        }
        this.emit();
        break;
      }

      case "peer-joined":
        // Either a new device, or one of ours coming back on a new socket after
        // a reload. `reach` tells those apart by whether the link still works.
        await this.reach(signal.id);
        this.emit();
        break;

      case "peer-left": {
        // A socket blip is not a departure: the peer connection is direct and
        // outlives the socket that arranged it. But a channel reading "open"
        // proves only that *this* end is willing — it stays open against a
        // device that was frozen or killed — so an open channel is not enough
        // to overrule the room. Ask, and let the sweep drop it if no answer
        // comes back within DEPARTED_GRACE_MS.
        const link = this.links.get(signal.id);
        if (!link) break;
        if (link.channel?.readyState !== "open") {
          this.dropLink(signal.id, "That device left the room.");
          break;
        }
        link.departedAt = Date.now();
        this.control(link, { kind: "ping" });
        break;
      }

      case "offer": {
        const link = this.ensureLink(signal.from);
        // We never renegotiate, so an offer for a pair we already hold means the
        // other side rebuilt — after a reload, say. Replace our end too; the old
        // connection is, by their account, already gone.
        if (!this.isLive(link) || !link.pc) {
          this.teardownPeer(link, "That device reconnected, so the transfer stopped.");
          await this.createPeer(link, false);
        }
        const pc = link.pc;
        if (!pc || pc.signalingState !== "stable") return;
        await pc.setRemoteDescription(signal.sdp);
        await this.flushIce(link);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        this.signal({ kind: "answer", sdp: answer, to: link.id });
        break;
      }

      case "answer": {
        const link = this.links.get(signal.from);
        const pc = link?.pc;
        // Only an offer of ours can be answered. Anything else is an answer to
        // a connection we have already torn down and replaced.
        if (!link || !pc || pc.signalingState !== "have-local-offer") return;
        await pc.setRemoteDescription(signal.sdp);
        await this.flushIce(link);
        break;
      }

      case "ice": {
        const link = this.links.get(signal.from);
        if (!link) return;
        if (link.pc?.remoteDescription) {
          try {
            await link.pc.addIceCandidate(signal.candidate);
          } catch {
            /* a stale candidate after renegotiation is harmless */
          }
        } else {
          link.pendingIce.push(signal.candidate);
        }
        break;
      }
    }
  }

  private async flushIce(link: Link): Promise<void> {
    const pc = link.pc;
    if (!pc) return;
    const pending = link.pendingIce.splice(0);
    for (const candidate of pending) {
      try {
        await pc.addIceCandidate(candidate);
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * Whether this pair has a connection worth keeping. Both halves matter: a
   * channel can read "open" over a peer connection that has already failed, and
   * a connected peer connection can have no usable channel yet.
   */
  private isLive(link: Link): boolean {
    const state = link.pc?.connectionState;
    if (state === undefined || state === "failed" || state === "closed") return false;
    return link.channel?.readyState === "open";
  }

  /**
   * Which side of a pair sends the offer. Comparing ids gives both devices the
   * same answer without either having to know who arrived first, which is what
   * makes rebuilding a dead connection safe: there is no moment where both
   * sides offer at once and have to unpick the glare.
   */
  private shouldOffer(peerId: string): boolean {
    return this.selfId > peerId;
  }

  /**
   * Make sure there is a working connection to `peerId`, rebuilding a dead one.
   * Called for everyone in the room on arrival and for each new peer after —
   * so whichever of the two devices notices first, the pair gets repaired.
   */
  private async reach(peerId: string): Promise<void> {
    if (peerId === this.selfId) return;
    const link = this.ensureLink(peerId);
    if (this.isLive(link)) return;
    if (!this.shouldOffer(peerId)) {
      // Their turn. They see us join too, and will offer; nothing to do but
      // keep the link so the offer has somewhere to land.
      return;
    }
    this.teardownPeer(link, "That connection was rebuilt, so the transfer stopped.");
    await this.createPeer(link, true);
  }

  /**
   * Throw away this pair's connection while keeping the peer itself. Whatever
   * was in flight over it is gone — the bytes stop mid-file — so anything that
   * was counting on it is failed here rather than left to time out.
   */
  private teardownPeer(link: Link, reason: string): void {
    const channel = link.channel;
    const pc = link.pc;
    link.channel = null;
    link.pc = null;
    link.pendingIce.length = 0;
    link.queue.length = 0;
    link.sending = null;
    link.incoming = null;
    link.wasOpen = false;
    if (link.state === "connected") link.state = "connecting";
    this.failAwaiting(link.id, reason);
    // Detached first, so the handlers do not read this as the peer leaving.
    if (channel) {
      channel.onopen = null;
      channel.onclose = null;
      channel.onmessage = null;
      try {
        channel.close();
      } catch {
        /* already gone */
      }
    }
    if (pc) {
      pc.onicecandidate = null;
      pc.onconnectionstatechange = null;
      pc.ondatachannel = null;
      try {
        pc.close();
      } catch {
        /* already gone */
      }
    }
  }

  private ensureLink(peerId: string): Link {
    let link = this.links.get(peerId);
    if (!link) {
      link = {
        id: peerId,
        name: null,
        label: "",
        kind: "computer",
        state: "connecting",
        pc: null,
        channel: null,
        files: [],
        pendingIce: [],
        incoming: null,
        queue: [],
        sending: null,
        lastSeen: Date.now(),
        departedAt: null,
        wasOpen: false,
      };
      this.links.set(peerId, link);
    }
    return link;
  }

  private dropLink(peerId: string, reason: string): void {
    const link = this.links.get(peerId);
    if (!link) return;
    this.links.delete(peerId);
    const hadConnection = link.wasOpen;
    this.teardownPeer(link, reason);
    // Anything we were pulling from that device is not going to finish.
    for (const meta of link.files) {
      const download = this.downloads.get(meta.id);
      if (download && !download.done) {
        this.downloads.set(meta.id, { ...download, failed: reason });
      }
      this.awaiting.delete(meta.id);
    }
    for (const set of this.taken.values()) set.delete(peerId);
    this.emit();

    // Losing a device we were actually talking to means our picture of the room
    // is out of date. Re-join and let `welcome` say who is really here: it also
    // repairs the case where the peer is still in the room and only the
    // connection died, which no other event would tell us about.
    if (hadConnection && !this.closed && Date.now() - this.lastRejoin > REJOIN_COOLDOWN_MS) {
      this.rejoin();
    }
  }

  /** Fail every transfer that was riding on one peer, with a reason worth reading. */
  private failAwaiting(linkId: string, reason: string): void {
    for (const [fileId, pending] of this.awaiting) {
      if (pending.linkId !== linkId) continue;
      this.awaiting.delete(fileId);
      const download = this.downloads.get(fileId);
      if (download && !download.done && !download.failed) {
        this.downloads.set(fileId, { ...download, failed: reason });
      }
    }
  }

  // ---- peer connections -----------------------------------------------------

  private async createPeer(link: Link, offerer: boolean): Promise<void> {
    const pc = new RTCPeerConnection({ iceServers: STUN_SERVERS });
    link.pc = pc;

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.signal({ kind: "ice", candidate: event.candidate.toJSON(), to: link.id });
      }
    };

    pc.onconnectionstatechange = () => {
      switch (pc.connectionState) {
        case "connected":
          link.state = "connected";
          break;
        case "failed":
          link.state = "failed";
          this.failDownloadsFrom(
            link,
            "Those two devices could not reach each other directly. Files never pass through our server, so when a network blocks device-to-device traffic there is no way through.",
          );
          break;
        case "disconnected":
          link.state = "connecting";
          break;
        default:
          return;
      }
      this.emit();
    };

    pc.ondatachannel = (event) => this.bindChannel(link, event.channel);

    if (offerer) {
      // Ordered and reliable: file bytes must arrive intact and in sequence.
      this.bindChannel(link, pc.createDataChannel("room", { ordered: true }));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      this.signal({ kind: "offer", sdp: offer, to: link.id });
    }
  }

  private bindChannel(link: Link, channel: RTCDataChannel): void {
    link.channel = channel;
    channel.binaryType = "arraybuffer";
    channel.bufferedAmountLowThreshold = LOW_WATER;

    channel.onopen = () => {
      link.state = "connected";
      link.wasOpen = true;
      link.lastSeen = Date.now();
      link.departedAt = null;
      this.control(link, {
        kind: "hello",
        name: this.selfName,
        label: this.selfLabel,
        deviceKind: this.selfKind,
        files: this.myMetas(),
      });
      this.emit();
    };
    channel.onclose = () => {
      if (this.closed || !this.links.has(link.id)) return;
      // The channel is the real presence signal; when it closes the device has
      // left (or its connection died), whatever the signalling socket says.
      this.dropLink(link.id, "The connection to that device closed.");
    };
    channel.onmessage = (event) => this.onChannelMessage(link, event);
  }

  /**
   * Take a peer's list of what it is sharing, and settle up with anything of
   * theirs we were still waiting for.
   *
   * A `hello` can shrink the list every bit as sharply as a `files` does — a
   * device that reloaded introduces itself again with nothing to share, because
   * a File handle cannot survive a reload — so both go through here, rather
   * than only the message that looks like an update.
   */
  private setPeerFiles(link: Link, files: FileMeta[]): void {
    const still = new Set(files.map((f) => f.id));
    for (const meta of link.files) {
      if (still.has(meta.id)) continue;
      this.awaiting.delete(meta.id);
      const download = this.downloads.get(meta.id);
      if (download && !download.done) {
        this.downloads.set(meta.id, {
          ...download,
          failed: "The owner stopped sharing this file.",
        });
      }
    }
    link.files = files;
  }

  private control(link: Link, message: Control): void {
    if (link.channel?.readyState === "open") link.channel.send(JSON.stringify(message));
  }

  private myMetas(): FileMeta[] {
    return [...this.mine.values()].map((entry) => entry.meta);
  }

  private failDownloadsFrom(link: Link, reason: string): void {
    for (const meta of link.files) {
      const download = this.downloads.get(meta.id);
      if (download && !download.done && !download.failed) {
        this.downloads.set(meta.id, { ...download, failed: reason });
      }
    }
    link.incoming = null;
  }

  private onChannelMessage(link: Link, event: MessageEvent): void {
    // Anything arriving at all is proof the device is still there — and the only
    // proof available, since a data channel does not close when a peer freezes.
    link.lastSeen = Date.now();
    link.departedAt = null;
    const data = event.data;

    if (typeof data === "string") {
      let control: Control;
      try {
        control = JSON.parse(data) as Control;
      } catch {
        return;
      }
      switch (control.kind) {
        case "hello":
          link.name = control.name;
          link.label = control.label;
          link.kind = control.deviceKind;
          this.setPeerFiles(link, control.files);
          this.emit();
          break;
        case "files":
          this.setPeerFiles(link, control.files);
          this.emit();
          break;
        case "ping":
          this.control(link, { kind: "pong" });
          break;
        case "pong":
          // Nothing to do: stamping lastSeen above was the whole point.
          break;
        case "request":
          link.queue.push(control.id);
          void this.serve(link);
          break;
        case "begin":
          link.incoming = { meta: control.meta, chunks: [], bytes: 0 };
          this.awaiting.set(control.meta.id, {
            linkId: link.id,
            deadline: Date.now() + STALL_TIMEOUT_MS,
          });
          this.downloads.set(control.meta.id, {
            fileId: control.meta.id,
            received: 0,
            size: control.meta.size,
            done: false,
            failed: null,
            blobUrl: null,
            blob: null,
          });
          this.emit();
          break;
        case "end": {
          const incoming = link.incoming;
          if (!incoming || incoming.meta.id !== control.id) return;
          this.awaiting.delete(control.id);
          const blob = new Blob(incoming.chunks, { type: incoming.meta.mime });
          this.downloads.set(control.id, {
            fileId: control.id,
            received: blob.size,
            size: incoming.meta.size,
            done: true,
            failed: null,
            blobUrl: URL.createObjectURL(blob),
            blob,
          });
          link.incoming = null;
          this.emit();
          break;
        }
        case "cancel": {
          this.awaiting.delete(control.id);
          const download = this.downloads.get(control.id);
          if (download) this.downloads.set(control.id, { ...download, failed: control.reason });
          if (link.incoming?.meta.id === control.id) link.incoming = null;
          this.emit();
          break;
        }

        case "moved":
          // A device here opened the room to someone elsewhere. Follow it, so
          // the whole room stays one room and the newcomer meets everybody.
          // moveTo() ignores a code we already hold, which is what stops this
          // from echoing around the mesh forever.
          this.moveTo(control.code);
          break;
      }
      return;
    }

    const incoming = link.incoming;
    if (!incoming) return;
    const chunk = data as ArrayBuffer;
    incoming.chunks.push(chunk);
    incoming.bytes += chunk.byteLength;
    // Bytes are moving, so the transfer has another STALL_TIMEOUT_MS to live.
    const pending = this.awaiting.get(incoming.meta.id);
    if (pending) pending.deadline = link.lastSeen + STALL_TIMEOUT_MS;
    const download = this.downloads.get(incoming.meta.id);
    if (download) {
      this.downloads.set(incoming.meta.id, { ...download, received: incoming.bytes });
    }
    this.emitProgress();
  }

  // ---- serving my files -----------------------------------------------------

  private async serve(link: Link): Promise<void> {
    if (link.sending !== null) return;
    while (link.queue.length > 0) {
      const fileId = link.queue.shift();
      if (!fileId) break;
      await this.sendFile(link, fileId);
    }
  }

  private async sendFile(link: Link, fileId: string): Promise<void> {
    const entry = this.mine.get(fileId);
    if (!entry) {
      this.control(link, { kind: "cancel", id: fileId, reason: "That file is no longer shared." });
      return;
    }
    const channel = link.channel;
    if (!channel || channel.readyState !== "open") return;

    link.sending = fileId;
    this.emit();
    this.control(link, { kind: "begin", meta: entry.meta });

    const { file } = entry;
    let offset = 0;
    let ok = true;

    while (offset < file.size) {
      if (channel.readyState !== "open") {
        ok = false;
        break;
      }
      // Wait for the buffer to drain instead of queueing the whole file.
      //
      // With a deadline: a receiver that vanished mid-transfer never drains it,
      // and an await that never settles leaves `sending` set for the life of the
      // page, wedging every request queued behind this one.
      if (channel.bufferedAmount > HIGH_WATER) {
        const drained = await new Promise<boolean>((resolve) => {
          let timer: ReturnType<typeof setTimeout> | undefined;
          const onLow = () => {
            if (timer !== undefined) clearTimeout(timer);
            channel.removeEventListener("bufferedamountlow", onLow);
            resolve(true);
          };
          timer = setTimeout(() => {
            channel.removeEventListener("bufferedamountlow", onLow);
            resolve(false);
          }, STALL_TIMEOUT_MS);
          channel.addEventListener("bufferedamountlow", onLow);
        });
        if (!drained) {
          ok = false;
          break;
        }
        continue;
      }
      const buffer = await file.slice(offset, offset + CHUNK_SIZE).arrayBuffer();
      try {
        channel.send(buffer);
      } catch {
        ok = false;
        break;
      }
      offset += buffer.byteLength;
    }

    if (ok) {
      this.control(link, { kind: "end", id: fileId });
      let set = this.taken.get(fileId);
      if (!set) {
        set = new Set();
        this.taken.set(fileId, set);
      }
      set.add(link.id);
    } else {
      // Say so rather than going quiet: if the channel is merely slow rather
      // than dead, this is what stops the other end waiting out its own clock.
      this.control(link, {
        kind: "cancel",
        id: fileId,
        reason: "The file stopped sending part-way. Ask for it again.",
      });
    }
    link.sending = null;
    this.emit();
  }

  // ---- liveness -------------------------------------------------------------

  private startSweep(): void {
    if (this.sweepTimer !== null || this.closed) return;
    this.lastSweep = Date.now();
    this.sweepTimer = setInterval(() => this.sweep(), SWEEP_INTERVAL_MS);
  }

  private stopSweep(): void {
    if (this.sweepTimer === null) return;
    clearInterval(this.sweepTimer);
    this.sweepTimer = null;
  }

  /**
   * Ask everyone whether they are still there, and give up on whatever has
   * stopped answering — peers and transfers alike. This is the only thing in
   * the room that notices a device which went away without saying so, which is
   * the normal way a phone leaves.
   *
   * The first thing checked is our own clock. A frozen tab runs no timers, so a
   * long gap between sweeps means *we* were away rather than that the room went
   * quiet; counting that gap against anybody would empty the room on the way
   * back and fail every transfer in it.
   */
  private sweep(): void {
    const now = Date.now();
    const gap = now - this.lastSweep;
    this.lastSweep = now;
    if (gap > SILENCE_MS) {
      for (const link of this.links.values()) link.lastSeen = now;
      for (const pending of this.awaiting.values()) pending.deadline = now + STALL_TIMEOUT_MS;
      return;
    }

    let changed = false;

    for (const link of [...this.links.values()]) {
      if (link.channel?.readyState === "open") this.control(link, { kind: "ping" });
      const silent = now - link.lastSeen;
      if (link.departedAt !== null && silent > DEPARTED_GRACE_MS) {
        // The room said this device left and it has not spoken up since.
        this.dropLink(link.id, "That device left the room.");
        changed = true;
      } else if (link.wasOpen && silent > SILENCE_MS) {
        // A pair that worked and has gone silent: frozen, killed, or off the
        // network. A pair that never opened is left alone — that is a
        // connection which could not be made, and "adrift" is the honest word
        // for it, not something to quietly sweep out of the list.
        this.dropLink(link.id, "That device stopped responding.");
        changed = true;
      }
    }

    for (const [fileId, pending] of [...this.awaiting]) {
      if (now <= pending.deadline) continue;
      this.awaiting.delete(fileId);
      const link = this.links.get(pending.linkId);
      if (link?.incoming?.meta.id === fileId) link.incoming = null;
      const download = this.downloads.get(fileId);
      if (download && !download.done && !download.failed) {
        this.downloads.set(fileId, {
          ...download,
          failed:
            download.received === 0
              ? "That device never started sending. It may have gone to sleep, or closed the page."
              : "The file stopped arriving part-way. Ask for it again.",
        });
      }
      changed = true;
    }

    if (changed) this.emit();
  }

  // ---- public actions -------------------------------------------------------

  share(files: File[]): void {
    for (const file of files) {
      const meta: FileMeta = {
        id: newId("f"),
        name: file.name,
        size: file.size,
        mime: file.type || "application/octet-stream",
      };
      this.mine.set(meta.id, { file, meta });
    }
    this.broadcastFiles();
    this.emit();
  }

  unshare(fileId: string): void {
    this.mine.delete(fileId);
    this.taken.delete(fileId);
    this.broadcastFiles();
    this.emit();
  }

  private broadcastFiles(): void {
    const files = this.myMetas();
    for (const link of this.links.values()) this.control(link, { kind: "files", files });
  }

  /** Ask a file's owner to send it. Returns false if the owner is not reachable. */
  download(fileId: string): boolean {
    for (const link of this.links.values()) {
      const meta = link.files.find((f) => f.id === fileId);
      if (!meta) continue;
      const existing = this.downloads.get(fileId);
      if (existing?.blobUrl) URL.revokeObjectURL(existing.blobUrl);
      if (link.channel?.readyState !== "open") {
        this.awaiting.delete(fileId);
        this.downloads.set(fileId, {
          fileId,
          received: 0,
          size: meta.size,
          done: false,
          failed: "That device is not connected yet.",
          blobUrl: null,
          blob: null,
        });
        this.emit();
        return false;
      }
      this.downloads.set(fileId, {
        fileId,
        received: 0,
        size: meta.size,
        done: false,
        failed: null,
        blobUrl: null,
        blob: null,
      });
      // "open" above means only that this end is willing — the request can still
      // vanish into a channel whose other end is asleep. Give it a deadline, so
      // the row ends up saying what happened instead of sitting at 0% forever.
      this.awaiting.set(fileId, { linkId: link.id, deadline: Date.now() + START_TIMEOUT_MS });
      this.control(link, { kind: "request", id: fileId });
      this.emit();
      return true;
    }
    return false;
  }

  /** Pull every file in the room this device has not taken yet. Returns the ids requested. */
  downloadAll(): string[] {
    const requested: string[] = [];
    for (const link of this.links.values()) {
      for (const meta of link.files) {
        const existing = this.downloads.get(meta.id);
        if (existing && (existing.done || !existing.failed)) continue;
        if (this.download(meta.id)) requested.push(meta.id);
      }
    }
    return requested;
  }

  leave(): void {
    this.closed = true;
    this.generation += 1;
    this.stopSweep();
    this.awaiting.clear();
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    for (const link of this.links.values()) {
      link.channel?.close();
      link.pc?.close();
    }
    this.links.clear();
    this.ws?.close();
    this.ws = null;
    for (const download of this.downloads.values()) {
      // Each held blob is a whole file sitting in memory.
      if (download.blobUrl) URL.revokeObjectURL(download.blobUrl);
    }
    this.status = "closed";
    this.detail = null;
    this.emit();
  }
}
